/**
 * Lightweight, consent-aware analytics helper.
 *
 * - Sends events to GA4 through gtag() and mirrors them to window.dataLayer
 *   (so any tag manager listening there also receives them).
 * - No-op when analytics consent has not been granted.
 * - SSR-safe: silently does nothing on the server.
 */
import { readConsent } from "@/lib/consent/store";
import type {
  CtaEventName,
  FormFailureReason,
  SubmitEventName,
  TrackedEventName,
} from "@/lib/analytics/cta-events";

type AnalyticsEvent = {
  event: string;
  [key: string]: unknown;
};

declare global {
  interface Window {
    dataLayer?: AnalyticsEvent[];
    gtag?: (...args: unknown[]) => void;
  }
}

function hasAnalyticsConsent(): boolean {
  return !!readConsent()?.categories.analytics;
}

export function track(event: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  window.gtag?.("event", event, params);
}

/** Read the GA4 client id from the _ga cookie (e.g. "GA1.1.123.456" -> "123.456"). */
function readGaClientId(): string | null {
  const match = /(?:^|;\s*)_ga=GA\d+\.\d+\.([\d]+\.[\d]+)/.exec(document.cookie);
  return match?.[1] ?? null;
}

/**
 * Track a CTA click both client-side (gtag) and server-side (GA4 Measurement
 * Protocol via /api/public/ga-event), so the event survives ad blockers and
 * page unloads. Both calls require analytics consent.
 */
export function trackCta(name: CtaEventName, params: Record<string, unknown> = {}): void {
  trackEvent(name, params);
}

/**
 * Track a completed form submission, client-side and server-side, using the
 * same transport as CTA clicks so click-to-submit conversion rates line up.
 */
export function trackFormSubmit(
  name: SubmitEventName,
  params: { form_name: string; page_path?: string; locale?: "en" | "bg"; placement?: string },
): void {
  const pagePath = params.page_path ?? (typeof window !== "undefined" ? window.location.pathname : undefined);
  // keep the legacy generic event for existing reports
  track("form_submit", { formName: params.form_name, pagePath });
  if (typeof window !== "undefined") {
    // let the form engagement tracker know this form converted
    window.dispatchEvent(
      new CustomEvent("perceptica:form-submitted", { detail: { form_name: params.form_name } }),
    );
  }
  trackEvent(name, { ...params, page_path: pagePath });
}

/** Fired when a visitor first interacts with a form (funnel step 2). */
export function trackFormStart(params: {
  form_name: string;
  page_path?: string;
  locale?: "en" | "bg";
}): void {
  trackEvent("form_start", withPagePath(params));
}

/**
 * Fired when a visitor started a form but left the page (or navigated away)
 * without a successful submission. `time_in_form` is in whole seconds.
 */
export function trackFormAbandon(params: {
  form_name: string;
  time_in_form: number;
  fields_filled?: number;
  page_path?: string;
  locale?: "en" | "bg";
}): void {
  trackEvent("form_abandon", withPagePath(params));
}

/**
 * Fired when a submission attempt fails, with a coarse reason code plus the
 * HTTP status (when there was one) so backend/validation issues affecting
 * conversion can be diagnosed.
 */
export function trackFormFailure(params: {
  form_name: string;
  reason: FormFailureReason;
  error_code?: string | number;
  page_path?: string;
  locale?: "en" | "bg";
}): void {
  trackEvent("form_submit_failure", {
    ...withPagePath(params),
    error_code: params.error_code === undefined ? undefined : String(params.error_code).slice(0, 60),
  });
}

/** Fired when a visitor clicks a link leading to another website. */
export function trackOutboundClick(params: {
  link_url: string;
  link_domain: string;
  link_text?: string;
  link_location?: string;
  page_path?: string;
  locale?: "en" | "bg";
}): void {
  trackEvent("outbound_click", { ...withPagePath(params), outbound: true });
}

/** Fired once when a tracked product video starts or reaches completion. */
export function trackVideoEngagement(
  name: "video_start" | "video_complete",
  params: { video_name: string; page_path?: string; locale?: "en" | "bg" },
): void {
  trackEvent(name, withPagePath(params));
}

function withPagePath<T extends { page_path?: string }>(params: T): T & { page_path?: string } {
  return {
    ...params,
    page_path: params.page_path ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
  };
}

function trackEvent(name: TrackedEventName, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;

  track(name, params);

  const body = JSON.stringify({
    name,
    clientId: readGaClientId(),
    params: {
      link_text: params.link_text,
      link_url: params.link_url,
      page_path: params.page_path,
      page_location: params.page_location,
      locale: params.locale,
      form_name: params.form_name,
      reason: params.reason,
      error_code: params.error_code,
      time_in_form: params.time_in_form,
      fields_filled: params.fields_filled,
      link_domain: params.link_domain,
      link_location: params.link_location,
      outbound: params.outbound,
      video_name: params.video_name,
      placement: params.placement,
    },
  });

  try {
    // keepalive so the request survives the navigation the click triggers.
    void fetch("/api/public/ga-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never block navigation on analytics */
  }
}
